import React, { useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Typography, Space, Select, DatePicker, Input, message, InputNumber, Modal, Switch } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { LeftOutlined, UserAddOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import StandardTable from '../../../components/common/StandardTable';
import Button from '../../../components/common/Button';
import { useGetMatClntPipingBom, useGetMatClntWhInventory, useGetMatClntWarehouses, useGetVwMatClntRequestItemOutstanding } from '../../../hooks/genericHooks';
import type { VwMatClntWhInventory, MatClntIssueRequestItem, MatWarehouses, VwMatClntRequestItemOutstanding } from '../../../types';
import { insertHeaderWithItems, getTableNextSequenceValue } from '../../../services/api';
import { PersonnelSelect, WPermitSelect, ActivitySelections } from '../../../components/modals';

const { Title, Text } = Typography;

type ItemRow = VwMatClntWhInventory & { key: string };

type BasketEntry = {
  row: ItemRow;
  qty: number;
};

type PipingBomBasketRow = {
  key: string;
  itemcode: string;
  description?: string | null;
  size_text?: string | null;
  unit?: string | null;
  design_qty?: number | null;
  warehouse_id?: number | null;
  onhand_qty?: number | null;
  available: boolean;
};

export default function MatClntIssueRequestCreate() {
  const navigate = useNavigate();

  // Data
  const { data: invData = [], isLoading } = useGetMatClntWhInventory() as any;
  const { data: whData = [] } = useGetMatClntWarehouses() as any;
  const { data: bomData = [] } = useGetMatClntPipingBom() as any;
  const { data: outData = [] } = useGetVwMatClntRequestItemOutstanding() as any;

  const unwrapRows = <T,>(payload: any): T[] => {
    if (Array.isArray(payload)) return payload as T[];
    const v1 = payload?.data;
    if (Array.isArray(v1)) return v1 as T[];
    const v2 = payload?.data?.data;
    if (Array.isArray(v2)) return v2 as T[];
    return [] as T[];
  };

  const items: VwMatClntWhInventory[] = unwrapRows<VwMatClntWhInventory>(invData);
  const warehouses: MatWarehouses[] = unwrapRows<MatWarehouses>(whData);
  const bomRows: any[] = unwrapRows<any>(bomData);
  const outstandingRows: VwMatClntRequestItemOutstanding[] = unwrapRows<VwMatClntRequestItemOutstanding>(outData);

  const whOptions = useMemo(() => (warehouses || []).map(w => ({ value: w.id, label: w.name })), [warehouses]);
  const whMap = useMemo(() => {
    const m = new Map<number, string>();
    (warehouses || []).forEach(w => m.set(w.id, w.name));
    return m;
  }, [warehouses]);

  // Header controls
  const [requesterDisplay, setRequesterDisplay] = useState<string>('');
  const [requesterCode, setRequesterCode] = useState<string | null>(null);
  const [date, setDate] = useState<string | null>(dayjs().format('YYYY-MM-DD'));
  const [notes, setNotes] = useState<string>('');
  const [materialGroup, setMaterialGroup] = useState<string>('');
  const [section, setSection] = useState<string>('');
  const [lineNo, setLineNo] = useState<string>('');
  const [fromWarehouseId, setFromWarehouseId] = useState<number | undefined>(undefined);

  const [pipingOnly, setPipingOnly] = useState<boolean>(false);

  const [wpOpen, setWpOpen] = useState(false);
  const [wpRefNum, setWpRefNum] = useState<string>('');
  const [wpDesc, setWpDesc] = useState<string>('');
  const [wpActCode, setWpActCode] = useState<string>('');
  const [wpActName, setWpActName] = useState<string>('');

  const [actOpen, setActOpen] = useState(false);
  const [actCombined, setActCombined] = useState<string>('');
  const [actCode, setActCode] = useState<string>('');
  const [actName, setActName] = useState<string>('');

  const finalActCodeDisplay = wpRefNum ? (wpActCode || '') : (actCode || '');
  const finalActNameDisplay = wpRefNum ? (wpActName || '') : (actName || '');

  const [personnelOpen, setPersonnelOpen] = useState(false);

  // Filters
  const [search, setSearch] = useState<string>('');
  const [groupFilter, setGroupFilter] = useState<string>('');
  const [unitFilter, setUnitFilter] = useState<string>('');

  const groupOptions = useMemo(() => {
    const s = (x: any) => (x == null ? '' : String(x)).trim();
    const by = new Map<string, string>();
    (items || [])
      .filter(r => (fromWarehouseId == null || r.warehouse_id === fromWarehouseId))
      .filter(r => !unitFilter || s(r.unit).toLowerCase() === s(unitFilter).toLowerCase())
      .forEach(r => {
        const raw = s((r as any).material_group);
        if (!raw) return;
        const k = raw.toLowerCase();
        if (!by.has(k)) by.set(k, raw);
      });
    return Array.from(by.values()).sort((a,b)=>a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [items, unitFilter, fromWarehouseId]);

  const unitOptions = useMemo(() => {
    const s = (x: any) => (x == null ? '' : String(x)).trim();
    const by = new Map<string, string>();
    (items || [])
      .filter(r => (fromWarehouseId == null || r.warehouse_id === fromWarehouseId))
      .filter(r => !groupFilter || s((r as any).material_group).toLowerCase() === s(groupFilter).toLowerCase())
      .forEach(r => {
        const raw = s(r.unit);
        if (!raw) return;
        const k = raw.toLowerCase();
        if (!by.has(k)) by.set(k, raw);
      });
    return Array.from(by.values()).sort((a,b)=>a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [items, groupFilter, fromWarehouseId]);

  const bomSectionOptions = useMemo(() => {
    const s = new Set<string>();
    (bomRows || [])
      .filter(r => !lineNo || String(r?.line_no ?? '').trim() === String(lineNo).trim())
      .forEach(r => {
        const v = String(r?.section ?? '').trim();
        if (v) s.add(v);
      });
    return Array.from(s).sort((a,b)=>a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [bomRows, lineNo]);

  const bomLineNoOptions = useMemo(() => {
    const s = new Set<string>();
    (bomRows || [])
      .filter(r => !section || String(r?.section ?? '').trim() === String(section).trim())
      .forEach(r => {
        const v = String(r?.line_no ?? '').trim();
        if (v) s.add(v);
      });
    return Array.from(s).sort((a,b)=>a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [bomRows, section]);

  useEffect(() => {
    if (section && !bomSectionOptions.some(o => o.value === section)) {
      setSection('');
    }
  }, [bomSectionOptions, section]);

  useEffect(() => {
    if (lineNo && !bomLineNoOptions.some(o => o.value === lineNo)) {
      setLineNo('');
    }
  }, [bomLineNoOptions, lineNo]);

  const pipingItemcodeSet = useMemo(() => {
    if (!pipingOnly) return null;
    if (!section || !lineNo) return new Set<string>();
    const s = new Set<string>();
    (bomRows || [])
      .filter(r => String(r?.section ?? '').trim() === String(section).trim())
      .filter(r => String(r?.line_no ?? '').trim() === String(lineNo).trim())
      .forEach(r => {
        const code = String(r?.itemcode ?? '').trim();
        if (code) s.add(code);
      });
    return s;
  }, [pipingOnly, bomRows, section, lineNo]);

  // Basket
  const [basket, setBasket] = useState<Record<string, BasketEntry>>({});
  const basketItems = useMemo(() => Object.values(basket), [basket]);

  const pipingBomRows = useMemo(() => {
    if (!pipingOnly) return [] as any[];
    if (!section || !lineNo) return [] as any[];
    return (bomRows || [])
      .filter(r => String(r?.section ?? '').trim() === String(section).trim())
      .filter(r => String(r?.line_no ?? '').trim() === String(lineNo).trim());
  }, [pipingOnly, bomRows, section, lineNo]);

  const pipingBomAgg = useMemo(() => {
    const by = new Map<string, { itemcode: string; description?: string | null; unit?: string | null; size_text?: string | null; design_qty: number }>();
    (pipingBomRows || []).forEach(r => {
      const itemcode = String(r?.itemcode ?? '').trim();
      if (!itemcode) return;
      const prev = by.get(itemcode);
      const qty = typeof r?.qty === 'number' ? r.qty : (r?.qty == null ? 0 : Number(r.qty));
      const safeQty = Number.isFinite(qty) ? qty : 0;
      const sizeParts = [r?.size1, r?.size2, r?.size3, r?.size4, r?.size5]
        .map((x: any) => (x == null ? '' : String(x)).trim())
        .filter(Boolean);
      const size_text = sizeParts.length > 0 ? sizeParts.join(' x ') : null;
      const desc = (r?.short_desc == null ? null : String(r.short_desc));
      const unit = (r?.unit == null ? null : String(r.unit));
      if (!prev) {
        by.set(itemcode, { itemcode, description: desc, unit, size_text, design_qty: safeQty });
      } else {
        prev.design_qty += safeQty;
        if (!prev.description && desc) prev.description = desc;
        if (!prev.unit && unit) prev.unit = unit;
        if (!prev.size_text && size_text) prev.size_text = size_text;
      }
    });
    return Array.from(by.values()).sort((a, b) => a.itemcode.localeCompare(b.itemcode));
  }, [pipingBomRows]);

  const invByItemcodeForWh = useMemo(() => {
    const m = new Map<string, VwMatClntWhInventory>();
    (items || [])
      .filter(r => fromWarehouseId == null || r.warehouse_id === fromWarehouseId)
      .forEach(r => {
        if (!m.has(r.itemcode)) m.set(r.itemcode, r);
      });
    return m;
  }, [items, fromWarehouseId]);

  const pipingBasketRows: PipingBomBasketRow[] = useMemo(() => {
    if (!pipingOnly) return [];
    return (pipingBomAgg || []).map(b => {
      const inv = invByItemcodeForWh.get(b.itemcode);
      const whId = fromWarehouseId ?? (inv?.warehouse_id ?? null);
      const onhand = inv?.onhand_qty ?? null;
      const available = !!inv && Number(inv.onhand_qty || 0) > 0;
      return {
        key: `${b.itemcode}-${whId ?? 'NA'}`,
        itemcode: b.itemcode,
        description: inv?.description ?? b.description ?? null,
        size_text: (inv as any)?.size_text ?? b.size_text ?? null,
        unit: inv?.unit ?? b.unit ?? null,
        design_qty: b.design_qty,
        warehouse_id: whId,
        onhand_qty: onhand,
        available,
      };
    });
  }, [pipingOnly, pipingBomAgg, invByItemcodeForWh, fromWarehouseId]);

  const [pipingReqQty, setPipingReqQty] = useState<Record<string, number>>({});
  const [pipingExcludedKeys, setPipingExcludedKeys] = useState<string[]>([]);
  const pipingExcludedSet = useMemo(() => new Set(pipingExcludedKeys), [pipingExcludedKeys]);

  useEffect(() => {
    if (!pipingOnly) return;
    setPipingReqQty(prev => {
      const next: Record<string, number> = {};
      (pipingBasketRows || []).forEach(r => {
        const k = String(r.itemcode);
        const existing = prev[k];
        if (typeof existing === 'number') {
          next[k] = existing;
          return;
        }
        const d = typeof r.design_qty === 'number' ? r.design_qty : Number(r.design_qty || 0);
        const safeD = Number.isFinite(d) ? d : 0;
        const max = r.available ? Number(r.onhand_qty || 0) : 0;
        next[k] = r.available ? Math.max(0, Math.min(safeD, max)) : 0;
      });
      return next;
    });
  }, [pipingOnly, pipingBasketRows]);

  const pipingBasketVisibleRows = useMemo(() => {
    return (pipingBasketRows || []).filter(r => !pipingExcludedSet.has(String(r.key)));
  }, [pipingBasketRows, pipingExcludedSet]);

  const pipingIssuedQtyByItemcode = useMemo(() => {
    const m = new Map<string, number>();
    if (!pipingOnly) return m;
    const sec = String(section || '').trim();
    const ln = String(lineNo || '').trim();
    if (!sec || !ln) return m;
    (outstandingRows || []).forEach(r => {
      if (String(r.material_group || '').toLowerCase() !== 'piping') return;
      if (String(r.section || '').trim() !== sec) return;
      if (String(r.line_no || '').trim() !== ln) return;
      const code = String(r.itemcode || '').trim();
      if (!code) return;
      const v = Number(r.issued_qty || 0);
      const prev = m.get(code) || 0;
      m.set(code, prev + (Number.isFinite(v) ? v : 0));
    });
    return m;
  }, [pipingOnly, section, lineNo, outstandingRows]);

  const selectedCount = useMemo(() => {
    return pipingOnly ? pipingBasketVisibleRows.length : basketItems.length;
  }, [pipingOnly, pipingBasketVisibleRows.length, basketItems.length]);

  const filtered: ItemRow[] = useMemo(() => {
    const searchVal = (search || '').trim().toLowerCase();
    const phraseMatches = Array.from(searchVal.matchAll(/"([^"]+)"/g)).map(m => m[1].trim()).filter(Boolean);
    const remaining = searchVal.replace(/"[^"]+"/g, ' ').trim();
    const tokens = remaining.split(/\s+/).filter(Boolean);
    const s = (x: any) => (x == null ? '' : String(x)).trim();

    const matchesSearch = (r: VwMatClntWhInventory) => {
      if (tokens.length === 0 && phraseMatches.length === 0) return true;
      const hay = `${r.itemcode} ${r.description || ''} ${r.size_text || ''} ${r.unit || ''} ${(r as any).material_group || ''}`.toLowerCase();
      if (phraseMatches.length > 0) return phraseMatches.every(p => hay.includes(p));
      return tokens.every(t => hay.includes(t));
    };

    return (items || [])
      .filter(r => (fromWarehouseId == null || r.warehouse_id === fromWarehouseId))
      .filter(r => !groupFilter || s((r as any).material_group).toLowerCase() === s(groupFilter).toLowerCase())
      .filter(r => !unitFilter || s(r.unit).toLowerCase() === s(unitFilter).toLowerCase())
      .filter(r => !pipingOnly || !pipingItemcodeSet || pipingItemcodeSet.has(String(r.itemcode)))
      .filter(r => Number(r.onhand_qty || 0) > 0)
      .filter(matchesSearch)
      .map(r => ({ ...r, key: `${r.itemcode}-${r.warehouse_id}` }));
  }, [items, search, groupFilter, unitFilter, fromWarehouseId, pipingOnly, pipingItemcodeSet]);

  const selectedKeys: React.Key[] = useMemo(() => {
    const keys = new Set(Object.keys(basket));
    return (filtered || []).filter(r => keys.has(r.key)).map(r => r.key);
  }, [basket, filtered]);

  const columns: ColumnsType<ItemRow> = useMemo(() => [
    { title: 'Itemcode', dataIndex: 'itemcode', key: 'itemcode', width: 70 },
    { title: 'Description', dataIndex: 'description', key: 'description',width: 400, ellipsis: true },
    { title: 'Size', dataIndex: 'size_text', key: 'size_text', width: 70 },
    { title: 'Unit', dataIndex: 'unit', key: 'unit', width: 60 },
    { title: 'Group', dataIndex: 'material_group', key: 'material_group', width: 70 },
    { title: 'Warehouse', dataIndex: 'warehouse_id', key: 'warehouse_id', width: 120, render: (v:number)=> whMap.get(v) || v },
    { title: 'Outstanding', dataIndex: 'requests', key: 'requests', width: 90, align: 'right', render: (v: any) => {
      const raw = Number(v ?? 0);
      if (!Number.isFinite(raw)) return 0;
      return Math.floor(raw * 1000) / 1000;
    } },
    { title: 'On-hand', dataIndex: 'onhand_qty', key: 'onhand_qty', width: 80, align: 'right', render: (v: any) => {
      const raw = Number(v ?? 0);
      if (!Number.isFinite(raw)) return 0;
      return Math.floor(raw * 1000) / 1000;
    }, sorter: (a, b) => (a.onhand_qty ?? 0) - (b.onhand_qty ?? 0) },
  ], [whMap]);

  // Validations
  const isRequesterValid = (requesterDisplay || '').trim().length > 0 && !!(requesterCode && String(requesterCode).trim().length > 0);
  const isDateValid = !!date;
  const isActivityValid = pipingOnly || ((finalActCodeDisplay || '').trim().length > 0 && (finalActNameDisplay || '').trim().length > 0);
  const isWarehouseValid = fromWarehouseId != null;
  const needsPipingFields = pipingOnly;
  const arePipingFieldsValid = !needsPipingFields || (
    (section || '').trim().length > 0 && (lineNo || '').trim().length > 0
  );
  const disableSubmit = !isRequesterValid || !isDateValid || !isActivityValid || !isWarehouseValid || !arePipingFieldsValid;

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    try {
      if (!requesterDisplay || requesterDisplay.trim() === '') { message.warning('Please select requester'); return; }
      if (!date) { message.warning('Please select Request Date'); return; }
      if (fromWarehouseId == null) { message.warning('Please select From Warehouse'); return; }
      const finalActCode = wpRefNum ? (wpActCode || '') : (actCode || '');
      const finalActName = wpRefNum ? (wpActName || '') : (actName || '');
      if (!pipingOnly) {
        if (!wpRefNum && !actCode) { message.warning('Please select a Work Permit or an Activity'); return; }
        if (!finalActCode || !finalActName) { message.warning('Missing final Activity Code/Name.'); return; }
      }
      if (needsPipingFields && !arePipingFieldsValid) { message.warning('Please fill Section and Line No'); return; }
      if (!pipingOnly && basketItems.length === 0) { message.warning('Please select at least one item'); return; }

      const candidates = pipingOnly
        ? (pipingBasketRows || [])
            .filter(r => r.available)
            .map(r => ({ row: r, qty: Number(pipingReqQty[String(r.itemcode)] || 0) }))
            .filter(x => x.qty > 0)
        : basketItems.filter(x => (x.qty || 0) > 0);
      if (candidates.length === 0) { message.warning('Please enter valid quantities'); return; }

      setSubmitting(true);

      // Generate CREQ-<7 digits>
      const nextId = await getTableNextSequenceValue('mat_clnt_issue_requests', 'id');
      if (!nextId) throw new Error('Failed to get next request id from sequence');
      const refNo = `CREQ-${String(nextId).padStart(7, '0')}`;

      // Ensure requester_name does not contain the requester code prefix
      const parseFromDisplay = (s: string) => {
        const str = String(s || '').trim();
        const m = str.match(/^([A-Za-z0-9_\-]+)\s*[-:]\s*(.+)$/);
        if (m) return { code: m[1].trim(), name: m[2].trim() };
        return { code: '', name: str };
      };
      const parsed = parseFromDisplay(requesterDisplay);
      const codeFromState = String(requesterCode || '').trim();
      const codeOnlyFromState = (() => {
        const m = codeFromState.match(/^([A-Za-z0-9_\-]+)/);
        return m ? m[1] : codeFromState;
      })();
      const finalRequesterCode = codeOnlyFromState || parsed.code;
      const sanitizedRequesterName = (() => {
        const base = (requesterDisplay || '').trim();
        const nameFromParsed = parsed.name;
        const codeToStrip = finalRequesterCode;
        if (!codeToStrip) return nameFromParsed || base;
        const re = new RegExp(`^${codeToStrip}\\s*[-:]?\\s*`, 'i');
        const stripped = base.replace(re, '').trim();
        return stripped || nameFromParsed || base;
      })();

      if (!finalRequesterCode || !sanitizedRequesterName) {
        message.warning('Requester parsing failed. Please re-select requester.');
        setSubmitting(false);
        return;
      }

      const header: any = {
        ref_no: refNo,
        requester: finalRequesterCode,
        requester_name: sanitizedRequesterName,
        req_date: date,
        notes: notes || null,
        material_group: (pipingOnly ? 'piping' : (materialGroup || null)),
        section: needsPipingFields ? (section || null) : null,
        line_no: needsPipingFields ? (lineNo || null) : null,
        spool_id: null,
        workpermit_ref_num: pipingOnly ? null : (wpRefNum || null),
        activity_code: pipingOnly ? null : finalActCode,
        activity_name: pipingOnly ? null : finalActName,
      };

      const itemsPayload: Partial<MatClntIssueRequestItem>[] = candidates.map((x: any) => ({
        ref_no: refNo,
        itemcode: x.row.itemcode,
        unit: (x.row.unit || '') as string,
        requested_qty: x.qty,
        warehouse_id: x.row.warehouse_id,
        size_text: (x.row as any)?.size_text ?? null,
        notes: ((x.row as any)?.description ?? null) as any,
      }));

      const trxResp = await insertHeaderWithItems({
        header_table: 'mat_clnt_issue_requests',
        header_data: header,
        items_table: 'mat_clnt_issue_request_items',
        items: itemsPayload,
      });
      const requestId = trxResp?.header_id || null;
      if (!requestId) throw new Error('Failed to create request (header id missing)');

      message.success(`Client Request created. Ref: ${refNo}. Lines: ${itemsPayload.length}`);
      try {
        // Fire event-based refresh for listeners
        if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
          window.dispatchEvent(new CustomEvent('mat_clnt_issue_requests:changed'));
          window.dispatchEvent(new CustomEvent('mat_clnt_issue_request_items:changed'));
        }
      } catch {}
      setBasket({});
      setPipingReqQty({});
      setNotes('');
      setMaterialGroup('');
      setSection('');
      setLineNo('');
      setFromWarehouseId(undefined);
      setPipingOnly(false);
    } catch (err: any) {
      console.error(err);
      message.error(err?.message || 'Failed to create client request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card bordered={false} bodyStyle={{ padding: 8 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <Space align="center" size={8}>
            <Button icon={<LeftOutlined />} onClick={() => navigate(-1)} type="text" aria-label="Back" />
            <div>
              <Title level={4} style={{ margin: 0 }}>Create Client Issue Request</Title>
              <Text type="secondary">Select lines and quantities, then raise a request</Text>
            </div>
          </Space>
        </Col>
      </Row>

      {/* Filters row */}
      <Row justify="start" align="middle" gutter={[12, 8]} style={{ marginBottom: 16 }}>
        <Col>
          <Space wrap>
            <Select
              placeholder="From Warehouse (required)"
              value={fromWarehouseId}
              onChange={(v) => {
                if (v == null) { setFromWarehouseId(v); return; }
                const targetWhName = whMap.get(v) || v;
                const toRemove = Object.values(basket).filter(x => x.row.warehouse_id !== v).length;
                if (toRemove > 0) {
                  Modal.confirm({
                    title: 'Change warehouse filter?',
                    content: `${toRemove} item(s) in basket are from other warehouse(s) and will be removed. Continue switching to '${targetWhName}'?`,
                    okText: 'Yes, switch',
                    cancelText: 'No',
                    onOk: () => {
                      setFromWarehouseId(v);
                      setBasket(prev => {
                        const next: typeof prev = {} as any;
                        Object.entries(prev).forEach(([k, val]) => {
                          if (val.row.warehouse_id === v) next[k] = val as any;
                        });
                        return next;
                      });
                      message.warning(`${toRemove} item(s) removed from basket due to warehouse filter change.`);
                    },
                  });
                } else {
                  setFromWarehouseId(v);
                }
              }}
              options={whOptions}
              status={isWarehouseValid ? undefined : 'error'}
              style={{ width: 220 }}
            />
            <Select
              placeholder="Group"
              allowClear
              showSearch
              value={groupFilter || undefined}
              onChange={(v) => {
                const next = v || '';
                setGroupFilter(next);
                setMaterialGroup(next);
              }}
              options={groupOptions}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
              disabled={pipingOnly}
              style={{ width: 200 }}
            />
            <Select
              placeholder="Unit"
              allowClear
              showSearch
              value={unitFilter || undefined}
              onChange={(v) => setUnitFilter(v || '')}
              options={unitOptions}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
              style={{ width: 160 }}
            />
            <Input
              placeholder="Search item/description"
              allowClear
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 260 }}
            />
          </Space>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} style={{ paddingLeft: 8, display: 'flex', justifyContent: 'flex-end' }}>
          <Space>
            <Button action="add" type="primary" onClick={handleSubmit} disabled={disableSubmit} loading={submitting}>{`Create Request (${selectedCount || 0})`}</Button>
            <Button onClick={() => {
              setSearch('');
              setGroupFilter('');
              setMaterialGroup('');
              setUnitFilter('');
              setFromWarehouseId(undefined);
              setBasket({});
              setPipingOnly(false);
              setSection('');
              setLineNo('');
            }}>Reset</Button>
          </Space>
        </Col>
      </Row>

      {/* Header inputs */}
      <Row gutter={[12, 8]} align="middle">
        <Col xs={24} sm={12} md={8} lg={6}>
          <Input
            placeholder="Requester"
            value={requesterDisplay}
            onChange={(e) => setRequesterDisplay(e.target.value)}
            suffix={<Button size="small" icon={<UserAddOutlined />} onClick={() => setPersonnelOpen(true)} />}
            status={isRequesterValid ? undefined : 'error'}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Space>
            <Text strong>Request Date</Text>
            <DatePicker
              value={date ? dayjs(date) : null}
              onChange={(d) => setDate(d ? d.format('YYYY-MM-DD') : null)}
              style={{ width: 160 }}
              status={isDateValid ? undefined : 'error'}
            />
          </Space>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Space>
            <Text>Piping only</Text>
            <Switch
              checked={pipingOnly}
              onChange={(checked) => {
                setPipingOnly(checked);
                if (checked) {
                  setMaterialGroup('piping');
                  setGroupFilter('piping');
                  setWpRefNum('');
                  setWpDesc('');
                  setWpActCode('');
                  setWpActName('');
                  setActCombined('');
                  setActCode('');
                  setActName('');
                } else {
                  setSection('');
                  setLineNo('');
                }
              }}
            />
          </Space>
        </Col>
        <Col xs={24} md={24} lg={24}>
          <Input.TextArea
            placeholder="Request notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={1}
            allowClear
          />
        </Col>
      </Row>

      {/* Conditional header fields for Piping */}
      {needsPipingFields && (
        <Row gutter={[12, 8]} style={{ marginTop: 8 }}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Select
              placeholder="Section"
              allowClear
              showSearch
              value={section || undefined}
              onChange={(v) => {
                setSection(v || '');
                setLineNo('');
              }}
              options={bomSectionOptions}
              status={section ? undefined : 'error'}
              style={{ width: '100%' }}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Select
              placeholder="Line No"
              allowClear
              showSearch
              value={lineNo || undefined}
              onChange={(v) => setLineNo(v || '')}
              options={bomLineNoOptions}
              status={lineNo ? undefined : 'error'}
              style={{ width: '100%' }}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
            />
          </Col>
        </Row>
      )}

      {/* Associations */}
      {!pipingOnly && (
        <>
          <Row gutter={[12, 8]} align="middle" style={{ marginTop: 8 }}>
            <Col span={24}>
              <Space align="center" wrap size="middle" style={{ width: '100%' }}>
                <Button style = {{width:200}} onClick={() => setWpOpen(true)}>Pick Work Permit</Button>
                {wpRefNum && (
                  <Button onClick={() => { setWpRefNum(''); setWpDesc(''); setWpActCode(''); setWpActName(''); }}>Clear WP</Button>
                )}

                <Input
                  placeholder="WP Ref - Desc"
                  value={wpRefNum ? `${wpRefNum} - ${wpDesc}` : ''}
                  readOnly
                  style={{ width: 600 }}
                />
                <Button style={{width:200}} onClick={() => setActOpen(true)} disabled={!!wpRefNum}>Pick Activity</Button>
                {actCode && !wpRefNum && (
                  <Button onClick={() => { setActCombined(''); setActCode(''); setActName(''); }}>Clear Activity</Button>
                )}
              </Space>
            </Col>
          </Row>
          <Row gutter={[12, 8]} style={{ marginTop: 8 }}>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Input placeholder="Activity Code" value={finalActCodeDisplay} readOnly status={finalActCodeDisplay ? undefined : 'error'} />
            </Col>
            <Col xs={24} sm={12} md={16} lg={18}>
              <Input placeholder="Activity Name" value={finalActNameDisplay} readOnly status={finalActNameDisplay ? undefined : 'error'} />
            </Col>
          </Row>
        </>
      )}

      {/* Basket Summary */}
      <Row style={{ marginTop: 12 }}>
        <Col span={24}>
          <Card
            size="small"
            title={
              <span>
                <ShoppingCartOutlined style={{ marginRight: 6 }} />
                {`Basket (${selectedCount})`}
              </span>
            }
            bodyStyle={{ padding: 8, background: '#fffbe6' }}
          >
            {pipingOnly ? (
              (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                    <colgroup>
                      <col style={{ width: 80 }} />
                      <col style={{ width: 320 }} />
                      <col style={{ width: 80 }} />
                      <col style={{ width: 45 }} />
                      <col style={{ width: 70 }} />
                      <col style={{ width: 70 }} />
                      <col style={{ width: 110 }} />
                      <col style={{ width: 80 }} />
                      <col style={{ width: 110 }} />
                      <col style={{ width: 60 }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '4px 4px' }}>Itemcode</th>
                        <th style={{ textAlign: 'left', padding: '4px 4px' }}>Description</th>
                        <th style={{ textAlign: 'left', padding: '4px 4px' }}>Size</th>
                        <th style={{ textAlign: 'left', padding: '4px 4px' }}>Unit</th>
                        <th style={{ textAlign: 'right', padding: '4px 4px' }}>Dsgn Qty</th>
                        <th style={{ textAlign: 'right', padding: '4px 4px' }}>Issued</th>
                        <th style={{ textAlign: 'left', padding: '4px 4px' }}>Warehouse</th>
                        <th style={{ textAlign: 'right', padding: '4px 4px' }}>On-hand</th>
                        <th style={{ textAlign: 'left', padding: '4px 4px' }}>Req Qty</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {pipingBasketVisibleRows.length === 0 ? (
                        <tr>
                          <td colSpan={10} style={{ padding: '6px 6px' }}>
                            <Text type="secondary">Select Section and Line No to load BOM items.</Text>
                          </td>
                        </tr>
                      ) : (
                        pipingBasketVisibleRows.map((r) => (
                          <tr
                            key={r.key}
                            style={r.available ? undefined : { background: '#fff1f0', color: '#cf1322' }}
                          >
                            <td style={{ padding: '3px 6px' }}>{r.itemcode}</td>
                            <td style={{ padding: '3px 6px', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.description || '-'}</td>
                            <td style={{ padding: '3px 6px', maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.size_text || '-'}</td>
                            <td style={{ padding: '3px 6px' }}>{r.unit || '-'}</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right' }}>{(r.design_qty ?? 0) as any}</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right' }}>{(() => {
                              const raw = Number(pipingIssuedQtyByItemcode.get(String(r.itemcode)) || 0);
                              if (!Number.isFinite(raw)) return 0;
                              return Math.floor(raw * 1000) / 1000;
                            })()}</td>
                            <td style={{ padding: '3px 6px' }}>{r.warehouse_id != null ? (whMap.get(Number(r.warehouse_id)) || r.warehouse_id) : '-'}</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right' }}>{(() => {
                              const raw = Number(r.onhand_qty ?? 0);
                              if (!Number.isFinite(raw)) return 0;
                              return Math.floor(raw * 1000) / 1000;
                            })()}</td>
                            <td style={{ padding: '3px 6px' }}>
                              <InputNumber
                                min={0}
                                max={r.available ? Number(r.onhand_qty || 0) : 0}
                                value={pipingReqQty[String(r.itemcode)] ?? 0}
                                disabled={!r.available}
                                onChange={(v) => {
                                  const q = typeof v === 'number' ? v : 0;
                                  setPipingReqQty(prev => ({ ...prev, [String(r.itemcode)]: q }));
                                }}
                                style={{ width: 120 }}
                              />
                            </td>
                            <td style={{ padding: '3px 6px' }}>
                              <Button
                                size="small"
                                danger
                                onClick={() => {
                                  setPipingExcludedKeys(prev => (prev.includes(String(r.key)) ? prev : [...prev, String(r.key)]));
                                  setPipingReqQty(prev => ({ ...prev, [String(r.itemcode)]: 0 }));
                                }}
                              >
                                Remove
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )
            ) : (
              (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                    <colgroup>
                      <col style={{ width: 80 }} />
                      <col style={{ width: 360 }} />
                      <col style={{ width: 90 }} />
                      <col style={{ width: 55 }} />
                      <col style={{ width: 140 }} />
                      <col style={{ width: 80 }} />
                      <col style={{ width: 110 }} />
                      <col style={{ width: 60 }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '4px 4px' }}>Itemcode</th>
                        <th style={{ textAlign: 'left', padding: '4px 4px' }}>Description</th>
                        <th style={{ textAlign: 'left', padding: '4px 4px' }}>Size</th>
                        <th style={{ textAlign: 'left', padding: '4px 4px' }}>Unit</th>
                        <th style={{ textAlign: 'left', padding: '4px 4px' }}>Warehouse</th>
                        <th style={{ textAlign: 'right', padding: '4px 4px' }}>On-hand</th>
                        <th style={{ textAlign: 'left', padding: '4px 4px' }}>Req Qty</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {basketItems.length === 0 ? (
                        <tr>
                          <td colSpan={8} style={{ padding: '6px 6px' }}>
                            <Text type="secondary">No items in basket. Select rows and set quantities to add.</Text>
                          </td>
                        </tr>
                      ) : (
                        basketItems.map(({ row, qty }) => (
                          <tr key={row.key}>
                            <td style={{ padding: '3px 6px' }}>{row.itemcode}</td>
                            <td style={{ padding: '3px 6px', maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(row as any).description || '-'}</td>
                            <td style={{ padding: '3px 6px', maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.size_text || '-'}</td>
                            <td style={{ padding: '3px 6px' }}>{row.unit}</td>
                            <td style={{ padding: '3px 6px' }}>{whMap.get(row.warehouse_id) || row.warehouse_id}</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right' }}>{(() => {
                              const raw = Number(row.onhand_qty ?? 0);
                              if (!Number.isFinite(raw)) return 0;
                              return Math.floor(raw * 1000) / 1000;
                            })()}</td>
                            <td style={{ padding: '3px 6px' }}>
                              <InputNumber
                                min={0}
                                max={row.onhand_qty}
                                value={typeof qty === 'number' ? qty : 0}
                                onChange={(v) => {
                                  const q = typeof v === 'number' ? v : 0;
                                  setBasket(prev => {
                                    const next = { ...prev } as Record<string, BasketEntry>;
                                    next[row.key] = { ...prev[row.key], row, qty: q };
                                    return next;
                                  });
                                }}
                                style={{ width: 120 }}
                              />
                            </td>
                            <td style={{ padding: '3px 6px' }}>
                              <Button size="small" danger onClick={() => setBasket(prev => { const n = { ...prev }; delete n[row.key]; return n; })}>Remove</Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </Card>
        </Col>
      </Row>

      <StandardTable<ItemRow>
        columns={columns}
        dataSource={filtered}
        rowKey={(r) => r.key}
        loading={isLoading}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        enableExport
        rowSelection={pipingOnly ? undefined : {
          selectedRowKeys: selectedKeys,
          onChange: (keys, rows) => {
            const want = new Set((keys || []).map(k => String(k)));
            setBasket(prev => {
              const next = { ...prev } as Record<string, BasketEntry>;
              (filtered || []).forEach(r => {
                const k = String(r.key);
                if (want.has(k)) {
                  if (!next[k]) {
                    next[k] = { row: r, qty: 0 };
                  } else {
                    if (typeof next[k].qty !== 'number') {
                      next[k] = { ...next[k], row: r, qty: 0 };
                      return;
                    }
                    // Safety: never silently default to full on-hand
                    const curQty = Number(next[k]?.qty ?? 0);
                    const curOnhand = Number(next[k]?.row?.onhand_qty ?? r?.onhand_qty ?? 0);
                    if (Number.isFinite(curQty) && Number.isFinite(curOnhand) && curQty > 0 && curQty === curOnhand) {
                      next[k] = { ...next[k], row: r, qty: 0 };
                    } else {
                      next[k] = { ...next[k], row: r };
                    }
                  }
                } else {
                  if (next[k]) delete next[k];
                }
              });
              return next;
            });
          },
          preserveSelectedRowKeys: true,
          getCheckboxProps: (record) => ({ disabled: (fromWarehouseId != null && record.warehouse_id !== fromWarehouseId) || (Number(record.onhand_qty || 0) <= 0) }),
        }}
        scroll={{ x: 'max-content' }}
      />

      <PersonnelSelect
        open={personnelOpen}
        multi={false}
        onCancel={() => setPersonnelOpen(false)}
        onOk={(selection: any) => {
          try {
            const parseCodeName = (val: string) => {
              const s = String(val || '').trim();
              const m = s.match(/^([A-Za-z0-9_\-]+)\s*[-:]\s*(.+)$/);
              if (m) return { code: m[1].trim(), name: m[2].trim() };
              return { code: s, name: s };
            };
            if (Array.isArray(selection)) {
              const first = selection[0];
              const second = selection[1];
              if (second) {
                setRequesterCode(String(first));
                setRequesterDisplay(String(second));
              } else {
                const parsed = parseCodeName(String(first));
                setRequesterCode(parsed.code);
                setRequesterDisplay(parsed.name);
              }
            } else if (typeof selection === 'object') {
              const code = String(selection?.pers_id ?? selection?.code ?? '');
              const displayRaw = String(selection?.full_name ?? selection?.name ?? '');
              const parsed = displayRaw ? { code, name: displayRaw } : parseCodeName(code);
              setRequesterCode((parsed.code || code) || null);
              setRequesterDisplay(parsed.name);
            } else if (typeof selection === 'string') {
              const parsed = parseCodeName(selection);
              setRequesterCode(parsed.code);
              setRequesterDisplay(parsed.name);
            }
          } catch {}
          setPersonnelOpen(false);
        }}
      />
      <WPermitSelect
        open={wpOpen}
        onCancel={() => setWpOpen(false)}
        onOk={([ref, desc, aCode, aName]) => {
          setWpRefNum(ref);
          setWpDesc(desc);
          setWpActCode(aCode);
          setWpActName(aName);
          setActCombined(`${aCode} - ${aName}`);
          setActCode(aCode);
          setActName(aName);
          setWpOpen(false);
        }}
      />
      <ActivitySelections
        open={actOpen}
        onCancel={() => setActOpen(false)}
        onOk={(sel) => {
          if (Array.isArray(sel)) {
            const [combined, code, name] = sel as [string, string, string, number?];
            setActCombined(combined || `${code} - ${name}`);
            setActCode(code);
            setActName(name);
          } else if (typeof sel === 'string') {
            setActCombined(sel);
            setActCode(sel);
            setActName('');
          }
          setWpRefNum('');
          setWpDesc('');
          setWpActCode('');
          setWpActName('');
          setActOpen(false);
        }}
      />
    </Card>
  );
}
